import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Go2RtcPlayer, buildTalkWsUrl } from '../lib/videoRTC';
import { touchPanicPresence } from '../api/panic';

const PRESENCE_HEARTBEAT_MS = 15000;

/**
 * Разговор — картинка, голос станции и микрофон Оператора одним потоком
 * `talk-<id>`. Отдельный поток ради видео забрал бы у станции вторую RTSP-сессию,
 * а их у неё считанные единицы (ADR-0006). Существование этого компонента и есть
 * Разговор: он поднимается на принятом инциденте и умирает вместе с ним.
 */
const PanicConversation = ({ incidentId }) => {
  const { t } = useTranslation();
  const translateRef = useRef(t);
  translateRef.current = t;

  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const micStreamRef = useRef(null);
  const [state, setState] = useState('connecting');
  const [error, setError] = useState(null);
  const [transmitting, setTransmitting] = useState(false);
  const [micReady, setMicReady] = useState(false);

  useEffect(() => {
    if (!incidentId) return undefined;

    let cancelled = false;
    const translate = translateRef.current;

    const pingPresence = () => {
      touchPanicPresence(incidentId).catch(() => {});
    };
    pingPresence();
    const presenceTimer = setInterval(pingPresence, PRESENCE_HEARTBEAT_MS);

    const start = async () => {
      setError(null);
      setState('connecting');

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
          },
          video: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        micStreamRef.current = stream;
        const micTrack = stream.getAudioTracks()[0];
        micTrack.enabled = false;
        setMicReady(true);

        if (!videoRef.current) return;

        const player = new Go2RtcPlayer(videoRef.current, {
          mode: 'webrtc',
          media: 'video,audio',
          playUnmuted: true,
          keepSignaling: true,
          micTrack,
          micStream: stream,
          onStateChange: (nextState, detail) => {
            setState(nextState);
            if (nextState === 'error') {
              setError(detail || translateRef.current('panic.talkError'));
            }
          },
        });

        playerRef.current = player;
        player.connect(buildTalkWsUrl(`/panic/incidents/${incidentId}/talk/ws`));
      } catch (err) {
        setError(err?.name === 'NotAllowedError'
          ? translate('panic.micDenied')
          : (err?.message || translate('panic.talkError')));
        setState('error');
      }
    };

    start();

    return () => {
      cancelled = true;
      clearInterval(presenceTimer);
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
      }
      setMicReady(false);
      setTransmitting(false);
    };
  }, [incidentId]);

  const setTalkTransmitting = useCallback((on) => {
    setTransmitting(on);
    playerRef.current?.setTransmitting(on);
  }, []);

  const enableSound = () => {
    playerRef.current?.resume().catch(() => {});
  };

  const isLive = state === 'live';
  const isBlocked = state === 'blocked';
  const canTransmit = micReady && (isLive || isBlocked);

  const beginTransmit = () => {
    if (!canTransmit) return;
    setTalkTransmitting(true);
  };

  const endTransmit = () => {
    setTalkTransmitting(false);
  };

  return (
    <div className="space-y-3">
      <div className="relative overflow-hidden rounded-2xl border border-brand-400/30 bg-black">
        <video ref={videoRef} className="aspect-video w-full object-contain" playsInline autoPlay />

        <div className="absolute left-3 top-3 inline-flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
          {isLive && (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-300/60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-300" />
            </span>
          )}
          {isLive ? t('panic.listening') : t('panic.talkConnecting')}
        </div>

        {isBlocked && (
          <button
            type="button"
            onClick={enableSound}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-white"
          >
            <span className="material-icons text-4xl">volume_off</span>
            <span className="text-sm font-semibold">{t('panic.enableSound')}</span>
            <span className="text-xs text-muted-300">{t('panic.soundBlocked')}</span>
          </button>
        )}
      </div>

      {error && <p className="text-xs text-critical">{error}</p>}

      <button
        type="button"
        disabled={!canTransmit}
        onMouseDown={beginTransmit}
        onMouseUp={endTransmit}
        onMouseLeave={endTransmit}
        onTouchStart={(e) => { e.preventDefault(); beginTransmit(); }}
        onTouchEnd={(e) => { e.preventDefault(); endTransmit(); }}
        className={`flex w-full select-none items-center justify-center gap-2 rounded-2xl px-6 py-4 text-sm font-semibold transition ${
          transmitting
            ? 'bg-critical text-white ring-2 ring-critical/60'
            : 'border border-surface-border bg-surface-subtle text-white hover:border-brand-400/40'
        } disabled:opacity-40`}
      >
        <span className="material-icons text-lg">{transmitting ? 'mic' : 'mic_none'}</span>
        {transmitting ? t('panic.stationHearsYou') : t('panic.holdToTalk')}
      </button>
    </div>
  );
};

export default PanicConversation;
